import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, CheckCircle, X, Award, MapPin, Calendar, Linkedin, Mail, Lock, Target, ArrowRight } from "lucide-react";
import Ryan from "../Images/ryan.png";
import Maxens from "../Images/maxen.jpg";
import Header from "./Header";
import useUserRole from "../hooks/useUserRole";
import AnimatedCard, { MotionItem } from "./AnimatedCard";

const EASE = [0.22, 1, 0.36, 1];

// Same fade/slide-in as MotionItem, but also staggers its own children -
// used for the benefits grid so each item animates in turn
const gridContainer = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASE, staggerChildren: 0.08 },
  },
};

const SessionBooking = ({ setIsSidebarOpen, isSidebarOpen }) => {
  const [bookingStatus, setBookingStatus] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCoachLink, setSelectedCoachLink] = useState(null);
  // UI only: which coach profile is shown on the page
  const [activeCoachIndex, setActiveCoachIndex] = useState(0);
  const { user, loading: userLoading, sessionsBookingLocked } = useUserRole();

  // Enhanced coach data with skills, social links, and additional info
  // https://cal.com/ryan-jebali/accompagnement-strategique-technique
  const coaches = [
    {
      name: "Ryan Jebali",
      title: "Fondateur et Directeur",
      description: "Ryan Jebali est le fondateur et directeur d'Orra Academy, spécialisée dans l'éducation basée sur l'IA et l'intégration de ChatGPT. Fort de son expérience en science des données et en conseil, il mène l'innovation à l'intersection de la technologie et de l'apprentissage.",
      calLink: "ryan-jebali/accompagnement-strategique-technique",
      imageUrl: Ryan || "https://placehold.co/100x100/FDBA74/ffffff?text=Ryan",
      location: "Paris, France",
      skills: [
        "Stratégie d'IA",
        "Intégration de ChatGPT",
        "Science des Données",
        "Conseil en Affaires",
        "Technologie Éducative",
        "Apprentissage Automatique"
      ],
      socialLinks: [
        { platform: "linkedin", url: "https://linkedin.com/in/ryanjebali", icon: Linkedin },
        { platform: "email", url: "mailto:contact@orra-academy.com", icon: Mail }
      ],
      specialties: ["Éducation en IA", "Conseil en Start-up", "Stratégie de Données"],
      experience: "8+ ans",
      languages: ["Anglais", "Français"]
    },
    {
      name: "Maxens Baron",
      title: "Directeur des Programmes d'IA",
      description: "Maxens supervise les programmes de cours d'IA à Orra Academy, en se concentrant sur Python, l'apprentissage automatique et l'architecture d'IA appliquée. En tant que co-fondateur et ingénieur IA, il a livré plus de 50 solutions LLM et d'automatisation dans diverses industries. ",
      calLink: "maxens-baron-aeef33/accompagnement-technique",
      imageUrl: Maxens || "https://placehold.co/100x100/A78BFA/ffffff?text=Max",
      location: "Lyon, France",
      skills: [
        "Programmation Python",
        "Apprentissage Automatique",
        "Apprentissage Profond",
        "Architecture d'IA",
        "Ingénierie des Données",
        "Réseaux Neuronaux"
      ],
      socialLinks: [
        { platform: "linkedin", url: "https://linkedin.com/in/maxensbaron", icon: Linkedin },
        { platform: "email", url: "mailto:contact@orra-academy.com", icon: Mail }
      ],
      specialties: ["Formation Technique", "Développement d'IA", "Révision de Code"],
      experience: "6+ ans",
      languages: ["Anglais", "Français"]
    }
  ];

  // Function to construct the Cal.com iframe source URL
  const getCalComIframeSrc = (calLink) => {
    const baseCalComUrl = `https://app.cal.com/${calLink}`;
    const queryParams = new URLSearchParams({
      layout: "month_view",
      theme: "dark",
      primaryColor: "6366f1",
      backgroundColor: "1a1a1a",
      textColor: "ffffff",
      ...(user?.displayName && { name: user?.displayName }),
      ...(user?.email && { email: user?.email }),
    });
    return `${baseCalComUrl}?${queryParams.toString()}`;
  };

  useEffect(() => {
    if (!isModalOpen) {
      setBookingStatus(null);
      setIframeLoaded(false);
    }
  }, [user?.uid, isModalOpen]);

  const handleBookNow = (coachCalLink) => {
    setSelectedCoachLink(coachCalLink);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCoachLink(null);
    setBookingStatus(null);
  };

  if (userLoading) {
    return (
      <div>
        <Header
          PageName={"Séances de réservation"}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
        <div className="w-full bg-gray-900 text-gray-400 p-8 text-center">
          Chargement…
        </div>
      </div>
    );
  }

  if (sessionsBookingLocked) {
    return (
      <div>
        <Header
          PageName={"Séances de réservation"}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
        <div className="w-full bg-gray-900 text-gray-200 p-8 flex flex-col items-center justify-center max-w-lg mx-auto text-center gap-4">
          <Lock className="w-12 h-12 text-indigo-400" aria-hidden />
          <h2 className="text-xl font-semibold text-white">
            Réservation non disponible
          </h2>
          <p className="text-gray-400 text-sm leading-relaxed">
            Les séances de réservation sont réservées aux parcours avec
            accompagnement 1:1. Pour toute question, contactez-nous via les
            canaux habituels.
          </p>
        </div>
      </div>
    );
  }

  const activeCoach = coaches[activeCoachIndex];
  const activeFirstName = activeCoach.name.split(" ")[0];

  const benefits = [
    { Icon: Award, title: "Coachs experts", text: "Des experts du secteur triés sur le volet avec une expérience avérée" },
    { Icon: Clock, title: "Planification flexible", text: "Réservez des séances qui correspondent parfaitement à votre emploi du temps" },
    { Icon: Target, title: "Apprentissage personnalisé", text: "Des conseils personnalisés en fonction de vos objectifs uniques" },
  ];

  return (
    <div>
      {/* smooth panel transition (respects reduced motion) */}
      <style>{`
        @keyframes pm-panel-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .pm-panel-in { animation: pm-panel-in .35s cubic-bezier(.22,1,.36,1) both; }
        @media (prefers-reduced-motion: reduce) { .pm-panel-in { animation: none; } }
      `}</style>

      <Header
        PageName={"Séances de réservation"}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <AnimatedCard
        tilt={false}
        effects="light"
        className="w-full justify-center bg-gray-900 text-gray-200 p-4"
      >
        <div className="max-w-6xl mx-auto py-4">
          {/* Page heading */}
          <MotionItem className="mb-8 text-left">
            <h2 className="text-3xl font-bold text-white">Réservez une séance avec un coach</h2>
            <p className="mt-2 text-sm text-slate-400">
              Choisissez un expert, puis réservez un créneau dans son calendrier.
            </p>
          </MotionItem>

          {/* Coach switcher */}
          <MotionItem className="mb-6">
            <div
              role="tablist"
              aria-label="Coachs"
              className="relative grid p-1 rounded-[16px] bg-white/[0.04] border border-white/10"
              style={{ gridTemplateColumns: `repeat(${coaches.length}, minmax(0, 1fr))` }}
            >
              {/* sliding indicator */}
              <span
                aria-hidden="true"
                className="absolute top-1 bottom-1 left-1 rounded-[12px] bg-gradient-to-r from-[#113BE1] via-[#4268FF] to-[#113BE1] shadow-lg shadow-[#113BE1]/30 transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)]"
                style={{
                  width: `calc((100% - 8px) / ${coaches.length})`,
                  transform: `translateX(${activeCoachIndex * 100}%)`,
                }}
              />
              {coaches.map((coach, index) => {
                const active = index === activeCoachIndex;
                return (
                  <button
                    key={coach.name}
                    type="button"
                    role="tab"
                    id={`coach-tab-${index}`}
                    aria-selected={active}
                    aria-controls={`coach-panel-${index}`}
                    onClick={() => setActiveCoachIndex(index)}
                    className={`relative z-10 flex h-16 items-center gap-3 rounded-[12px] px-3 text-left transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 ${
                      active ? "text-white" : "text-white/55 hover:text-white/85"
                    }`}
                  >
                    <img
                      src={coach.imageUrl}
                      alt=""
                      className="h-11 w-11 shrink-0 rounded-full object-cover border-2 border-white/60"
                    />
                    <span className="min-w-0">
                      <span className="block truncate font-semibold">{coach.name}</span>
                      <span
                        className={`block truncate text-xs transition-colors duration-300 ${
                          active ? "text-white/80" : "text-slate-400"
                        }`}
                      >
                        {coach.title}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </MotionItem>

          {/* Profile + booking panel */}
          <MotionItem className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            {/* Profile */}
            <section
              key={activeCoach.name}
              role="tabpanel"
              id={`coach-panel-${activeCoachIndex}`}
              aria-labelledby={`coach-tab-${activeCoachIndex}`}
              className="pm-panel-in rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-left sm:p-8"
            >
              <div className="mb-6 flex items-center gap-5">
                <div className="relative shrink-0">
                  <img
                    src={activeCoach.imageUrl}
                    alt={activeCoach.name}
                    className="h-24 w-24 rounded-2xl border-2 border-blue-500/60 object-cover"
                  />
                  <span className="absolute -bottom-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-gray-900 bg-green-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-white" />
                  </span>
                </div>
                <div className="min-w-0">
                  <h3 className="text-2xl font-bold text-white">{activeCoach.name}</h3>
                  <p className="mt-0.5 font-medium text-blue-400">{activeCoach.title}</p>
                  <p className="mt-1.5 flex items-center gap-1.5 text-sm text-slate-400">
                    <MapPin className="h-4 w-4" aria-hidden="true" />
                    {activeCoach.location}
                  </p>
                </div>
              </div>

              <p className="mb-6 text-sm leading-relaxed text-slate-300">{activeCoach.description}</p>

              <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <h4 className="mb-1 flex items-center gap-2 text-xs text-slate-400">
                    <Award className="h-4 w-4" aria-hidden="true" />
                    Expérience
                  </h4>
                  <p className="font-semibold text-white">{activeCoach.experience}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <h4 className="mb-1 text-xs text-slate-400">Langages</h4>
                  <p className="font-semibold text-white">{activeCoach.languages.join(", ")}</p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="mb-3 text-xs text-slate-400">Spécialités</h4>
                <div className="flex flex-wrap gap-2">
                  {activeCoach.specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="rounded-full bg-blue-500/15 px-3 py-1 text-sm font-medium text-blue-300"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="mb-3 text-xs text-slate-400">Connecter</h4>
                <div className="flex gap-2">
                  {activeCoach.socialLinks.map((link, index) => {
                    const IconComponent = link.icon;
                    return (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={link.platform}
                        className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-slate-300 transition-colors hover:border-blue-500/50 hover:bg-blue-500/10 hover:text-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        <IconComponent className="h-5 w-5" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Booking panel */}
            <aside
              key={`booking-${activeCoach.name}`}
              className="pm-panel-in rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-left lg:sticky lg:top-6"
            >
              <div className="mb-5 flex items-center gap-3">
                <img
                  src={activeCoach.imageUrl}
                  alt=""
                  className="h-10 w-10 border-2 border-white/60 rounded-full object-cover"
                />
                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">{activeCoach.name}</p>
                  <p className="flex items-center gap-1.5 text-xs text-green-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    En ligne
                  </p>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-white">Réserver une séance</h3>
              <p className="mb-5 mt-1.5 text-sm leading-relaxed text-slate-400">
                Choisissez un créneau dans le calendrier de {activeFirstName}. Réservez des séances qui
                correspondent parfaitement à votre emploi du temps.
              </p>

              <button
                onClick={() => handleBookNow(activeCoach.calLink)}
                className="flex w-full items-center justify-center gap-[4px] rounded-xl bg-gradient-to-r from-[#113BE1] to-[#4268FF] px-2 py-3 text-center text-[12px] font-semibold leading-snug text-white shadow-lg shadow-blue-600/25 ring-1 ring-inset ring-white/15 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-600/35 hover:brightness-110 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
              >
                <Calendar className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span>Réservez une séance avec {activeFirstName}</span>
              </button>
            </aside>
          </MotionItem>

          {/* Benefits */}
          <motion.div variants={gridContainer} className="mt-8 grid grid-cols-1 gap-4 text-left md:grid-cols-3">
            {benefits.map(({ Icon, title, text }) => (
              <MotionItem
                key={title}
                className="flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-5"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500/15">
                  <Icon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-semibold text-white">{title}</h3>
                  <p className="mt-1 text-sm text-slate-400">{text}</p>
                </div>
              </MotionItem>
            ))}
          </motion.div>
        </div>

        {/* Booking Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden border border-gray-700">
              {/* Modal Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-700 bg-gradient-to-r from-indigo-600/10 to-purple-600/10">
                <h3 className="text-lg font-bold text-white">
                  Réservez une séance avec{" "}
                  {coaches.find((c) => c.calLink === selectedCoachLink)?.name ||
                    "Coach"}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="p-2 rounded-full text-gray-400 hover:bg-gray-700 hover:text-white transition-all duration-200 hover:scale-110"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content (Cal.com iframe) */}
              <div
                className="flex-grow relative flex bg-gray-800"
                style={{ minHeight: "500px" }}
              >
                {!iframeLoaded && (
                  <div className="text-gray-400 bg-gray-800 w-full z-10 flex items-center justify-center">
                    <Clock className="w-8 h-8 animate-spin mr-3 inline-block" />
                    Chargement du calendrier...
                  </div>
                )}
                <iframe
                  src={getCalComIframeSrc(selectedCoachLink)}
                  style={{
                    border: "none",
                    display: iframeLoaded ? "block" : "none",
                  }}
                  className="w-full min-h-full"
                  onLoad={() => setIframeLoaded(true)}
                  title="Cal.com Booking Calendar"
                ></iframe>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-gray-700 text-center bg-gray-800/50">
                {bookingStatus === "loading" && (
                  <p className="text-blue-400 flex items-center justify-center gap-2 mb-2">
                    <Clock className="w-5 h-5 animate-spin" />Confirmation de la réservation...
                  </p>
                )}
                {bookingStatus === "success" && (
                  <p className="text-green-400 flex items-center justify-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5" />Réservation confirmée ! La feuille de route sera bientôt disponible.
                  </p>
                )}
                {bookingStatus === "error" && (
                  <p className="text-red-400 mb-2">
                    Erreur lors de la confirmation de la réservation. Veuillez réessayer ou contacter le support.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </AnimatedCard>
    </div>
  );
};

export default SessionBooking;